import { useCallback, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import {
  createInterviewReviewer,
  deleteInterviewReviewer,
  getAllInterviewReviewers,
} from '@/services/interview-reviewer';
import { chakraExtendedTheme } from '@/styles/theme';
import { FetchApiIds, CandidateTechStacksType } from '@/types';
import { InterviewReviewer } from '@/types/api/interview-reviewer';
import ChallengeReviewersComponent from './ChallengeReviewers.component';

export type EditReviewerData = {
  candidateId: string;
  candidateName: string;
  skills: CandidateTechStacksType[];
};

const ChallengeReviewersPage = (): JSX.Element => {
  const toast = useToastContext();

  const [isNewCandidateFormOpened, setIsNewCandidateFormOpened] = useState(false);
  const [isEditFormOpened, setIsEditFormOpened] = useState(false);

  const [reviewerToEdit, setReviewerToEdit] = useState<EditReviewerData | undefined>();

  /** Used to prevent cache of the reviewers list */
  const [refreshKey, setRefreshKey] = useState(0);

  const allReviewersResponse = useApiFetch(
    [FetchApiIds.ALL_REVIEWERS, refreshKey],
    getAllInterviewReviewers
  );

  const refreshReviewersList = useCallback((): void => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const createReviewerMutation = useApiSubmit<any, { personId: string }>(createInterviewReviewer);

  const deleteReviewerMutation = useApiSubmit<any, { interviewReviewerId: string }>(
    deleteInterviewReviewer
  );

  const onShowCandidateFormClick = (): void => {
    setIsNewCandidateFormOpened(true);
  };

  const onHideCandidateFormClick = (): void => {
    setIsNewCandidateFormOpened(false);
  };

  const onShowEditFormClick = (reviewer: InterviewReviewer): void => {
    if (!reviewer.person?.id) {
      return;
    }

    const reviewerSkillsSource: unknown[] = reviewer.person.skills || [];
    const reviewerSkills = reviewerSkillsSource.filter(
      (skill): skill is CandidateTechStacksType => {
        return typeof skill === 'object' && skill !== null && 'skill' in skill;
      }
    );

    setReviewerToEdit({
      candidateId: reviewer.person.id,
      candidateName: `${reviewer.person.firstName} ${reviewer.person.lastName}`,
      skills: reviewerSkills,
    });
    setIsEditFormOpened(true);
  };

  const onHideEditFormClick = (): void => {
    setIsEditFormOpened(false);
    setReviewerToEdit(undefined);
  };

  const onAddNewReviewer = async (candidateId: string): Promise<void> => {
    try {
      if (isEditFormOpened) {
        toast.setMessage('Reviewer skills have been updated successfully.');
        toast.setSeverity('success');
        toast.showToast();

        onHideEditFormClick();
        refreshReviewersList();
      } else {
        await createReviewerMutation.mutateAsync({
          personId: candidateId,
        });

        toast.setMessage('Challenge reviewer has been added successfully.');
        toast.setSeverity('success');
        toast.showToast();

        onHideCandidateFormClick();
        refreshReviewersList();
      }
    } catch (err) {
      const defaultErrorMessage = isEditFormOpened
        ? 'There was a problem updating the reviewer skills, please try later.'
        : 'There was a problem adding the reviewer, please try later.';
      const errorMessage = createReviewerMutation?.error?.response?.data?.message;
      toast.setMessage(errorMessage || defaultErrorMessage);
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const onDeleteReviewer = async (interviewReviewerId: string): Promise<void> => {
    try {
      await deleteReviewerMutation.mutateAsync({
        interviewReviewerId,
      });

      toast.setMessage('Challenge reviewer has been removed successfully.');
      toast.setSeverity('success');
      toast.showToast();

      allReviewersResponse.refetch();
    } catch (err) {
      const defaultErrorMessage = 'There was a problem removing the reviewer, please try later.';
      const errorMessage = deleteReviewerMutation?.error?.response?.data?.message;
      toast.setMessage(errorMessage || defaultErrorMessage);
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <ChallengeReviewersComponent
        isLoading={allReviewersResponse.isLoading}
        reviewers={allReviewersResponse.data || []}
        isNewCandidateFormOpened={isNewCandidateFormOpened}
        onShowCandidateFormClick={onShowCandidateFormClick}
        onHideCandidateFormClick={onHideCandidateFormClick}
        onAddNewReviewer={onAddNewReviewer}
        onDeleteReviewer={onDeleteReviewer}
        isEditFormOpened={isEditFormOpened}
        reviewerToEdit={reviewerToEdit}
        onHideEditFormClick={onHideEditFormClick}
        onShowEditFormClick={onShowEditFormClick}
      />
    </ChakraProvider>
  );
};

export default ChallengeReviewersPage;
